import { memo } from 'react';
import { IconCodeBranch } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { MatchType } from '@frontend/types/workflow-agent';

import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { ConditionNodeRenderer } from '../components/renderers/ConditionNodeRenderer/ConditionNodeRenderer';

/**
 * Configuration for the Condition node type.
 * Represents a single condition in conditional logic flow.
 */
export const conditionConfig = defineNode({
  description: {
    displayName: 'Condition',
    name: NODE_TYPE.CONDITION,
    description: 'Represents a single condition in conditional logic flow',
    iconInfo: {
      icon: IconCodeBranch,
      color: 'fg.information',
    },
    category: NodeTypeCategory.CONDITION,
    input: {
      type: NodeConnectionTypes.Main,
      filter: {
        allowedNodeTypes: [
          { type: NODE_TYPE.SPECIALIZED, maxConnections: 1 },
          { type: NODE_TYPE.CONDITIONAL, maxConnections: 1 },
        ],
      },
      maxConnections: 1,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
      minConnections: 1,
    },
    properties: [
      {
        displayName: 'Matching Condition',
        name: 'matching_condition',
        type: 'string',
        required: true,
        default: '',
        description: 'The condition value to match against',
      },
      {
        displayName: 'Match Type',
        name: 'match_type',
        type: 'options',
        required: true,
        default: MatchType.EQUALS,
        description: 'How to compare the condition value',
        options: [
          { name: 'Equals', value: MatchType.EQUALS },
          { name: 'Not Equals', value: MatchType.NOT_EQUALS },
          { name: 'Contains', value: MatchType.CONTAINS },
          { name: 'Not Contains', value: MatchType.NOT_CONTAINS },
          { name: 'Greater Than', value: MatchType.GREATER_THAN },
          { name: 'Less Than', value: MatchType.LESS_THAN },
          { name: 'Greater Than or Equals', value: MatchType.GREATER_THAN_OR_EQUALS },
          { name: 'Less Than or Equals', value: MatchType.LESS_THAN_OR_EQUALS },
          { name: 'Regex', value: MatchType.REGEX },
        ],
      },
    ],
    defaults: {
      name: 'Condition',
      parameters: {
        matching_condition: '',
        match_type: MatchType.EQUALS,
      },
    },
  },

  render: memo(function ConditionRenderer(props: NodeProps) {
    return <ConditionNodeRenderer {...props} />;
  }),
});
